import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaWhatsapp,
  FaMapMarkerAlt,
  FaFacebook,
  FaPhone,
  FaEnvelope,
  FaInstagram,
  FaCheckCircle,
  FaSpinner,
  FaExclamationTriangle
} from 'react-icons/fa';
import emailjs from '@emailjs/browser';
import Header from './components/Header';
import Footer from './components/Footer';
import { WHATSAPP_NUMBER } from './config/navItems';
import { useLanguage } from './i18n';

const ContactPage = () => {
  const { t, tArray } = useLanguage();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    interestLevel: '',
    serviceType: '',
    preferredContact: 'email'
  });
  const [formStatus, setFormStatus] = useState({
    loading: false,
    success: false,
    error: false,
    message: ''
  });

  useEffect(() => {
    document.title = "Contact - Wind Tunnel Coaching | Get in Touch";

    // Leer parámetros de la URL y prellenar el formulario
    const urlParams = new URLSearchParams(window.location.search);
    const service = urlParams.get('service');
    const level = urlParams.get('level');
    const message = urlParams.get('message');
    const event = urlParams.get('event');

    if (service || level || message || event) {
      setFormData(prev => ({
        ...prev,
        serviceType: service || prev.serviceType,
        interestLevel: level || prev.interestLevel,
        message: message || prev.message || (event ? `I'm interested in the event: ${event}` : prev.message)
      }));
    }
  }, []);

  const resetFormStatus = () => {
    setFormStatus({
      loading: false,
      success: false,
      error: false,
      message: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validar campos obligatorios
    if (!formData.name || !formData.email || !formData.message) {
      setFormStatus({
        loading: false,
        success: false,
        error: true,
        message: t('contact.form.requiredFieldsError')
      });
      return;
    }

    setFormStatus({
      loading: true,
      success: false,
      error: false,
      message: t('contact.form.sendingButton')
    });

    try {
      // Configurar EmailJS con tus credenciales
      const serviceId = 'service_24z9to6';
      const clientTemplateId = 'template_iforiub'; // Template para el cliente
      const adminTemplateId = 'template_b6143og';   // Template para el admin
      const publicKey = 'CqR0_WKL9ukUS9LaG';

      const timestamp = new Date().toLocaleString('es-ES', {
        timeZone: 'Europe/Madrid',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });

      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        phone: formData.phone || 'Not provided',
        message: formData.message,
        interest_level: formData.interestLevel || 'Not specified',
        service_type: formData.serviceType || 'General inquiry',
        preferred_contact: formData.preferredContact,
        timestamp: timestamp,
        to_name: 'Wind Tunnel Coaching Team',
        reply_to: formData.email
      };

      // Enviar email de confirmación al cliente
      await emailjs.send(serviceId, clientTemplateId, templateParams, publicKey);

      // Enviar notificación al admin
      await emailjs.send(serviceId, adminTemplateId, {
        ...templateParams,
        to_email: 'Tunnelcoaching1o1@gmail.com' // Email del admin
      }, publicKey);

      setFormStatus({
        loading: false,
        success: true,
        error: false,
        message: t('contact.form.successMessage')
      });

      // Limpiar el formulario
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: '',
        interestLevel: '',
        serviceType: '',
        preferredContact: 'email'
      });

    } catch (error) {
      console.error('EmailJS Error:', error);
      setFormStatus({
        loading: false,
        success: false,
        error: true,
        message: t('contact.form.errorMessage')
      });
    }
  };

  const handleWhatsAppRedirect = (message) => {
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodedMessage}`, '_blank');
  };

  // Get service options from translations
  const serviceOptions = tArray('contact.form.serviceOptions');
  const levelOptions = tArray('contact.form.levelOptions');

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-blue-900 to-gray-900">
      {/* Header Component */}
      <Header />

      {/* Hero Section */}
      <div className="pt-20 pb-12 md:pt-24 md:pb-16 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <motion.h1
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="text-3xl sm:text-4xl md:text-6xl font-bold text-white mb-4 md:mb-6"
          >
            {t('contact.title')} <span className="bg-gradient-to-r from-orange-500 to-red-500 bg-clip-text text-transparent">{t('contact.titleHighlight')}</span>
          </motion.h1>
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-base md:text-xl text-gray-300 max-w-3xl mx-auto"
          >
            {t('contact.subtitle')}
          </motion.p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8 md:py-16">
        <div className="grid lg:grid-cols-2 gap-12">

          {/* Contact Form */}
          <motion.div
            initial={{ x: -50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            className="bg-gray-800/50 rounded-2xl p-6 md:p-8 backdrop-blur-sm border border-orange-500/20"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
              {t('contact.form.title')}
            </h2>

            {/* Status Messages */}
            <AnimatePresence>
              {(formStatus.success || formStatus.error) && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className={`p-4 rounded-lg mb-6 flex items-center ${
                    formStatus.success
                      ? 'bg-green-500/20 border border-green-500/30 text-green-300'
                      : 'bg-red-500/20 border border-red-500/30 text-red-300'
                  }`}
                >
                  {formStatus.success ? (
                    <FaCheckCircle className="mr-3 flex-shrink-0" />
                  ) : (
                    <FaExclamationTriangle className="mr-3 flex-shrink-0" />
                  )}
                  <span>{formStatus.message}</span>
                  <button
                    onClick={resetFormStatus}
                    className="ml-auto text-white/60 hover:text-white"
                  >
                    ×
                  </button>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block mb-2 text-white font-medium">
                    {t('contact.form.nameLabel')} {t('common.required')}
                  </label>
                  <input
                    id="name"
                    type="text"
                    required
                    className="w-full p-3 rounded-lg bg-gray-700/50 border border-gray-600 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-white placeholder-gray-400"
                    placeholder={t('contact.form.namePlaceholder')}
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block mb-2 text-white font-medium">
                    {t('contact.form.emailLabel')} {t('common.required')}
                  </label>
                  <input
                    id="email"
                    type="email"
                    required
                    className="w-full p-3 rounded-lg bg-gray-700/50 border border-gray-600 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-white placeholder-gray-400"
                    placeholder={t('contact.form.emailPlaceholder')}
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="phone" className="block mb-2 text-white font-medium">
                    {t('contact.form.phoneLabel')}
                  </label>
                  <input
                    id="phone"
                    type="tel"
                    className="w-full p-3 rounded-lg bg-gray-700/50 border border-gray-600 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-white placeholder-gray-400"
                    placeholder={t('contact.form.phonePlaceholder')}
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  />
                </div>

                <div>
                  <label htmlFor="preferredContact" className="block mb-2 text-white font-medium">
                    {t('contact.form.preferredContactLabel')}
                  </label>
                  <select
                    id="preferredContact"
                    className="w-full p-3 rounded-lg bg-gray-700/50 border border-gray-600 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-white"
                    value={formData.preferredContact}
                    onChange={(e) => setFormData({...formData, preferredContact: e.target.value})}
                  >
                    <option value="email">{t('contact.form.preferredContactOptions.email')}</option>
                    <option value="phone">{t('contact.form.preferredContactOptions.phone')}</option>
                    <option value="whatsapp">{t('contact.form.preferredContactOptions.whatsapp')}</option>
                  </select>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="serviceType" className="block mb-2 text-white font-medium">
                    {t('contact.form.serviceLabel')}
                  </label>
                  <select
                    id="serviceType"
                    className="w-full p-3 rounded-lg bg-gray-700/50 border border-gray-600 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-white"
                    value={formData.serviceType}
                    onChange={(e) => setFormData({...formData, serviceType: e.target.value})}
                  >
                    <option value="">{t('contact.form.servicePlaceholder')}</option>
                    {serviceOptions.map((service, index) => (
                      <option key={index} value={service}>{service}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="interestLevel" className="block mb-2 text-white font-medium">
                    {t('contact.form.levelLabel')}
                  </label>
                  <select
                    id="interestLevel"
                    className="w-full p-3 rounded-lg bg-gray-700/50 border border-gray-600 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-white"
                    value={formData.interestLevel}
                    onChange={(e) => setFormData({...formData, interestLevel: e.target.value})}
                  >
                    <option value="">{t('contact.form.levelPlaceholder')}</option>
                    {levelOptions.map((level, index) => (
                      <option key={index} value={level}>{level}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="message" className="block mb-2 text-white font-medium">
                  {t('contact.form.messageLabel')} {t('common.required')}
                </label>
                <textarea
                  id="message"
                  rows="6"
                  required
                  className="w-full p-3 rounded-lg bg-gray-700/50 border border-gray-600 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-white placeholder-gray-400"
                  placeholder={t('contact.form.messagePlaceholder')}
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                ></textarea>
              </div>

              <button
                onClick={handleSubmit}
                disabled={formStatus.loading}
                className="w-full bg-gradient-to-r from-orange-500 to-red-500 text-white px-8 py-4 rounded-lg hover:shadow-lg hover:shadow-orange-500/50 transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center space-x-2 text-lg font-bold"
              >
                {formStatus.loading ? (
                  <>
                    <FaSpinner className="animate-spin" />
                    <span>{t('contact.form.sendingButton')}</span>
                  </>
                ) : (
                  <>
                    <FaEnvelope />
                    <span>{t('contact.form.submitButton')}</span>
                  </>
                )}
              </button>
            </div>
          </motion.div>

          {/* Contact Information */}
          <motion.div
            initial={{ x: 50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="space-y-8"
          >
            {/* Contact Details */}
            <div className="bg-gray-800/50 rounded-2xl p-6 md:p-8 backdrop-blur-sm border border-orange-500/20">
              <h3 className="text-2xl font-bold text-white mb-6">{t('contact.info.title')}</h3>

              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-orange-500 p-3 rounded-lg">
                    <FaEnvelope className="text-white text-xl" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-1">{t('contact.info.emailLabel')}</h4>
                    <a href="mailto:Tunnelcoaching1o1@gmail.com" className="text-gray-300 hover:text-orange-500 transition-colors">
                      Tunnelcoaching1o1@gmail.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-orange-500 p-3 rounded-lg">
                    <FaPhone className="text-white text-xl" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-1">{t('contact.info.phoneLabel')}</h4>
                    <a href="tel:+34643091350" className="text-gray-300 hover:text-orange-500 transition-colors">
                      +34 643 09 13 50
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-orange-500 p-3 rounded-lg">
                    <FaMapMarkerAlt className="text-white text-xl" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-1">{t('contact.info.locationLabel')}</h4>
                    <p className="text-gray-300">{t('contact.info.locationValue')}</p>
                    <p className="text-gray-400 text-sm">{t('contact.info.locationNote')}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Alternative Contact Methods */}
            <div className="bg-gray-800/50 rounded-2xl p-6 md:p-8 backdrop-blur-sm border border-orange-500/20">
              <h3 className="text-2xl font-bold text-white mb-6">{t('contact.alternative.title')}</h3>

              <div className="space-y-4">
                <button
                  onClick={() => handleWhatsAppRedirect("Hi! I'm interested in wind tunnel coaching. Can we talk?")}
                  className="w-full bg-green-600 hover:bg-green-700 text-white p-4 rounded-lg transition-all flex items-center justify-center space-x-3"
                >
                  <FaWhatsapp className="text-2xl" />
                  <span className="font-semibold">{t('contact.alternative.whatsappButton')}</span>
                </button>

                <div className="grid grid-cols-2 gap-4">
                  <a
                    href="https://www.instagram.com/alexmatei.fly?igsh=dTZxZXEwZ2Fpbzhp"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white p-4 rounded-lg transition-all flex items-center justify-center space-x-2"
                  >
                    <FaInstagram className="text-xl" />
                    <span>{t('contact.alternative.instagram')}</span>
                  </a>

                  <a
                    href="https://www.facebook.com/share/1CPQVa2pB9/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-blue-600 hover:bg-blue-700 text-white p-4 rounded-lg transition-all flex items-center justify-center space-x-2"
                  >
                    <FaFacebook className="text-xl" />
                    <span>{t('contact.alternative.facebook')}</span>
                  </a>
                </div>
              </div>
            </div>

            {/* Response Time */}
            <div className="bg-gradient-to-r from-orange-500/20 to-red-500/20 rounded-2xl p-6 border border-orange-500/30">
              <h3 className="text-xl font-bold text-white mb-3">{t('contact.responseTime.title')}</h3>
              <p className="text-gray-300">
                {t('contact.responseTime.description')}
              </p>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Footer Component */}
      <Footer />
    </div>
  );
};

export default ContactPage;
